/*
 * Age-group lists a request sends, and the backend's refusal of one. A write or send
 * refuses unless every requested age group is accessible and in the request's assessment,
 * and names the ones it rejected so the screen can point at them instead of asking the
 * user to guess. The saved selection spans assessments, so a request narrows it first.
 */
import _ from 'lodash';

import { pluralize, summarizeList } from './text.helpers';
import { readErrorExtensionList } from './graphql-error.helpers';

// Beyond this many names the tail collapses to "and N more" — a refused filter can
// span dozens, and a wall of names is no more actionable than a count.
const NAME_CAP = 3;

// The age groups the refusal rejected, as its error names them. Empty for any
// other failure, which the caller surfaces as a general error instead.
export const readInaccessibleAgeGroupIds = (error: unknown): string[] =>
  readErrorExtensionList<string>(error, 'inaccessibleAgeGroupIds');

// The rejected age groups as a noun phrase to drop into a sentence. Names them only when
// every id resolves — a partly-named list reads as the whole of a shorter one. A screen with
// no names for them (most: an age group it cannot reach is not in its lists) passes no lookup.
export const describeInaccessibleAgeGroups = (
  ids: string[],
  nameById: (id: string) => string | undefined = () => undefined
): string => {
  const names = ids.map(nameById).filter((name): name is string => !!name);

  if (!names.length || names.length !== ids.length) {
    return `${ids.length} ${pluralize(ids.length, 'age group')}`;
  }

  return summarizeList(names, NAME_CAP);
};

const OBJECT_ID_PATTERN = /^[0-9a-f]{24}$/i;

// Drops ids the server can't cast (legacy or hand-edited settings), which would fail a whole `$in` lookup.
export const keepWellFormedIds = (ids: string[]): string[] => ids.filter((id) => OBJECT_ID_PATTERN.test(id));

// The selected age groups that sit in `assessmentId`, in selection order. The saved selection
// keeps other assessments' picks, which an assessment-scoped request must not send.
export const selectAgeGroupIdsInAssessment = (
  selectedIds: string[],
  ageGroups: Pick<IAgeGroup, '_id' | 'assessmentId'>[],
  assessmentId: string
): string[] => {
  const inAssessment = new Set(
    ageGroups.filter((ageGroup) => ageGroup.assessmentId === assessmentId).map((ageGroup) => ageGroup._id)
  );

  return _.uniq(selectedIds.filter((id) => inAssessment.has(id)));
};

// A short badge for an age group: its bucket (e.g. "U11"), else the first three characters of its name.
export const ageGroupBadgeLabel = (ageGroup?: Pick<IAgeGroup, 'name' | 'bucket'> | null): string =>
  (ageGroup?.bucket?.trim() || ageGroup?.name?.trim().slice(0, 3) || '').toUpperCase();
